'use client';

import { Button } from '@savvy-kit/components/button';
import { Calendar } from '@savvy-kit/components/calendar';
import { Input } from '@savvy-kit/components/input';
import { Label } from '@savvy-kit/components/label';
import { Popover, PopoverContent, PopoverTrigger } from '@savvy-kit/components/popover';
import { cn } from '@savvy-kit/lib/utils';
import { format, formatISO, isValid, parse, parseISO } from 'date-fns';
import { isNil } from 'es-toolkit';
import { CalendarIcon } from 'lucide-react';

import type { DateTimeFilterMeta, DateTimeOperator } from '../../types/filter-meta';
import type { DateTimeFilterValue } from '../../types/filter-values';
import { useOperatorOptions } from './hooks/use-operator-options';
import { FilterActions } from './shared/filter-actions';
import { FilterOperatorSelect } from './shared/filter-operator-select';

export type DateTimeFilterProps = {
  readonly filterMeta?: DateTimeFilterMeta;
  readonly value: DateTimeFilterValue | undefined;
  readonly onChange: (value: DateTimeFilterValue | undefined) => void;
  readonly className?: string;
};

export function DateTimeFilter({ filterMeta, value, onChange, className }: DateTimeFilterProps) {
  const operators = useOperatorOptions('datetime', filterMeta);
  const operator: DateTimeOperator = value?.operator ?? 'between';

  function parseDateTimeIso(iso: string | undefined) {
    if (isNil(iso) || iso === '') {
      return { date: undefined, time: '' };
    }

    const parsed = parseISO(iso);

    if (!isValid(parsed)) {
      return { date: undefined, time: '' };
    }

    return {
      date: parsed,
      time: format(parsed, 'HH:mm'),
    };
  }

  function combineDateAndTime(date: Date, time: string) {
    const combined = parse(time, 'HH:mm', date);

    return formatISO(combined);
  }

  const single =
    value && value.operator !== 'isEmpty' && value.operator !== 'between'
      ? parseDateTimeIso(value.value)
      : { date: undefined, time: '' };
  const start = value && value.operator === 'between' ? parseDateTimeIso(value.start) : { date: undefined, time: '' };
  const end = value && value.operator === 'between' ? parseDateTimeIso(value.end) : { date: undefined, time: '' };

  return (
    <div className={cn('flex flex-col gap-3 p-3', className)}>
      <FilterOperatorSelect
        id="datetime-filter-operator"
        value={operator}
        options={operators}
        onChange={(op) => (op === 'isEmpty' ? onChange({ operator: 'isEmpty' }) : onChange(undefined))}
      />

      {operator !== 'isEmpty' && operator !== 'between' && (
        <div className="flex flex-col gap-1.5">
          <Label>Date & Time</Label>
          <div className="flex gap-2">
            <Popover>
              <PopoverTrigger
                render={
                  <Button
                    variant="outline"
                    size="sm"
                    className={cn(
                      'flex-1 justify-start text-left font-normal',
                      !single.date && 'text-muted-foreground',
                    )}
                  />
                }
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {single.date ? format(single.date, 'PP') : 'Pick date'}
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={single.date}
                  onSelect={(d) => d && onChange({ operator, value: combineDateAndTime(d, single.time || '00:00') })}
                  autoFocus
                />
              </PopoverContent>
            </Popover>
            <Input
              type="time"
              className="w-24"
              value={single.time}
              onChange={(e) =>
                single.date && onChange({ operator, value: combineDateAndTime(single.date, e.target.value) })
              }
            />
          </div>
        </div>
      )}

      {operator === 'between' && (
        <>
          <div className="flex flex-col gap-1.5">
            <Label>From</Label>
            <div className="flex gap-2">
              <Popover>
                <PopoverTrigger
                  render={
                    <Button
                      variant="outline"
                      size="sm"
                      className={cn(
                        'flex-1 justify-start text-left font-normal',
                        !start.date && 'text-muted-foreground',
                      )}
                    />
                  }
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {start.date ? format(start.date, 'PP') : 'Start date'}
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={start.date}
                    onSelect={(d) => {
                      if (!d) return;
                      const s = combineDateAndTime(d, start.time || '00:00');
                      const e = end.date ? combineDateAndTime(end.date, end.time || '23:59') : s;
                      onChange({ operator: 'between', start: s, end: e });
                    }}
                    autoFocus
                  />
                </PopoverContent>
              </Popover>
              <Input
                type="time"
                className="w-24"
                value={start.time}
                onChange={(e) => {
                  if (!start.date) return;
                  const s = combineDateAndTime(start.date, e.target.value);
                  const eVal = value?.operator === 'between' ? value.end : s;
                  onChange({ operator: 'between', start: s, end: eVal });
                }}
              />
            </div>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>To</Label>
            <div className="flex gap-2">
              <Popover>
                <PopoverTrigger
                  render={
                    <Button
                      variant="outline"
                      size="sm"
                      className={cn('flex-1 justify-start text-left font-normal', !end.date && 'text-muted-foreground')}
                    />
                  }
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {end.date ? format(end.date, 'PP') : 'End date'}
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={end.date}
                    onSelect={(d) => {
                      if (!d) return;
                      const e = combineDateAndTime(d, end.time || '23:59');
                      const s = start.date ? combineDateAndTime(start.date, start.time || '00:00') : e;
                      onChange({ operator: 'between', start: s, end: e });
                    }}
                    autoFocus
                  />
                </PopoverContent>
              </Popover>
              <Input
                type="time"
                className="w-24"
                value={end.time}
                onChange={(e) => {
                  if (!end.date) return;
                  const eVal = combineDateAndTime(end.date, e.target.value);
                  const sVal = value?.operator === 'between' ? value.start : eVal;
                  onChange({ operator: 'between', start: sVal, end: eVal });
                }}
              />
            </div>
          </div>
        </>
      )}

      {value && <FilterActions onClear={() => onChange(undefined)} />}
    </div>
  );
}
